import moment, { isMoment } from "moment";

type SortByType<T> = ((x: T) => string) | (keyof T & string);

/**
 * A function for sorting Arrays. String will be sorted in NaturalSort order. Array will not be mutated.
 *
 *
 * example:
 *
 * Alphanumeric Sorted:["1","10","100","11","111","112","1566"]
 *
 * Natural Sorted:["1","10","11","100","111","112","1566"]
 *
 * @param array Array which will be sorted.
 * @param sortBy Function or String to provide, to which sort by.
 * @param [desc=false] If true sorting will be descended.
 * @returns A copy of the sorted Array
 */
export function naturalSort<T>(array: T[] | readonly T[], sortBy: SortByType<T>, desc: boolean = false): T[] {
  const result: T[] = [...array];
  if (result.length == 0) return [];
  const comparerFunction: (a: T, b: T) => number = (a: T, b: T) => {
    let aEl: unknown;
    let bEl: unknown;

    if (isString(sortBy)) {
      aEl = !desc ? a[sortBy] : b[sortBy];
      bEl = !desc ? b[sortBy] : a[sortBy];
    }
    if (isPredicateFunction(sortBy)) {
      aEl = !desc ? sortBy(a) : sortBy(b);
      bEl = !desc ? sortBy(b) : sortBy(a);
    }

    if (aEl === null || aEl === undefined) aEl = "";
    if (bEl === null || bEl === undefined) bEl = "";

    if (typeof aEl === "number" || typeof aEl === "boolean") aEl = aEl.toString();
    if (typeof bEl === "number" || typeof bEl === "boolean") bEl = bEl.toString();

    if (typeof aEl === "string" && typeof bEl === "string") {
      if (moment(aEl, true).isValid() && moment(bEl, true).isValid()) {
        return moment(aEl).diff(bEl);
      } else {
        return aEl.localeCompare(bEl, undefined, {
          numeric: true,
          sensitivity: "base"
        });
      }
    } else if ((aEl instanceof Date || isMoment(aEl)) && (bEl instanceof Date || isMoment(bEl))) {
      return moment(aEl).diff(bEl);
    }

    throw new Error("NotCompareableTypes");
  };

  result.sort(comparerFunction);
  return result;
}

function isPredicateFunction<T>(e: SortByType<T>): e is (x: T) => any {
  return typeof e === "function";
}

function isString<T>(e: SortByType<T>): e is keyof T & string {
  return typeof e === "string";
}
